<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Applications"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      @refresh="loadData"
      @export="handleExport"
    />

    <div class="page-body">
      <div class="kpi-grid">
        <MetricCard label="App Registrations" :value="summary.appRegistrations" :state="summaryState" />
        <MetricCard label="Enterprise Apps" :value="summary.enterpriseApps" :state="summaryState" />
        <MetricCard label="Managed Identities" :value="summary.managedIdentities" :state="summaryState" />
        <MetricCard label="Multi-Tenant Apps" :value="summary.multiTenantApps" :state="summaryState" />
        <MetricCard label="No Owner" :value="summary.noOwnerApps" :state="summaryState" />
        <MetricCard label="Secrets Expiring &lt;30d" :value="summary.secretsExpiring30d" :state="summaryState" />
        <MetricCard label="Certs Expiring &lt;30d" :value="summary.certsExpiring30d" :state="summaryState" />
        <MetricCard label="Expired Credentials" :value="summary.expiredCredentials" :state="summaryState" />
        <MetricCard label="Disabled SPs" :value="summary.disabledSPs" :state="summaryState" />
        <MetricCard label="High-Privilege Permissions" :value="summary.appsWithFindings" :state="summaryState" />
        <MetricCard label="Apps with Findings" :value="summary.appsWithFindings" :state="summaryState" />
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>

      <h2 class="section-title">App Registrations</h2>
      <div v-if="tableState === 'loading'" class="loading-state">Loading...</div>
      <div v-else class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Application</th>
              <th>App ID</th>
              <th>Audience</th>
              <th>Owners</th>
              <th>Secrets</th>
              <th>Certificates</th>
              <th>Created</th>
              <th>Findings</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="app in apps" :key="app.appId">
              <td class="app-name">{{ app.displayName }}</td>
              <td class="app-id">{{ app.appId }}</td>
              <td>{{ app.signInAudience || '--' }}</td>
              <td>{{ formatCount(app.ownerCount) }}</td>
              <td :class="{ 'expiring-cell': app.expiringSecrets > 0 }">{{ app.activeSecrets || 0 }} ({{ app.expiringSecrets || 0 }})</td>
              <td :class="{ 'expiring-cell': app.expiringCerts > 0 }">{{ app.activeCerts || 0 }} ({{ app.expiringCerts || 0 }})</td>
              <td>{{ app.createdDateTime || '--' }}</td>
              <td>
                <FindingSeverityChip v-if="app.findingCount > 0" :severity="app.worstSeverity" />
                <span v-else>--</span>
              </td>
              <td><button class="detail-btn" @click="openApp(app.appId)">Details</button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 class="section-title">Credential Expiry</h2>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Application</th>
              <th>Type</th>
              <th>Credential</th>
              <th>Start</th>
              <th>End</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cred in credentials" :key="cred.credentialId">
              <td>{{ cred.appName }}</td>
              <td>{{ cred.credentialType }}</td>
              <td>{{ cred.displayName || '--' }}</td>
              <td>{{ cred.startDateTime || '--' }}</td>
              <td :class="{ 'expiry-soon': cred.daysUntilExpiry <= 30, 'expired': cred.daysUntilExpiry <= 0 }">
                {{ cred.endDateTime || '--' }}
              </td>
              <td>
                <span class="cred-status" :class="credStatusClass(cred.daysUntilExpiry)">
                  {{ credStatus(cred.daysUntilExpiry) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showDrawer" class="drawer-overlay" @click.self="closeDrawer">
      <div class="drawer">
        <div class="drawer-header">
          <h2>{{ drawerApp.displayName || 'Application Details' }}</h2>
          <button class="drawer-close" @click="closeDrawer">&times;</button>
        </div>

        <div v-if="drawerState === 'loading'" class="drawer-loading">Loading...</div>
        <div v-else-if="drawerState === 'error'" class="drawer-error">{{ drawerError }}</div>
        <div v-else class="drawer-body">
          <div class="drawer-details">
            <div class="detail-row"><span class="detail-label">App ID</span><span class="detail-value mono">{{ drawerApp.appId }}</span></div>
            <div class="detail-row"><span class="detail-label">Audience</span><span class="detail-value">{{ drawerApp.signInAudience || '--' }}</span></div>
            <div class="detail-row"><span class="detail-label">Created</span><span class="detail-value">{{ drawerApp.createdDateTime || '--' }}</span></div>
            <div class="detail-row"><span class="detail-label">Service Principal</span><span class="detail-value mono">{{ drawerApp.servicePrincipalId || 'None' }}</span></div>
            <div class="detail-row"><span class="detail-label">Secrets</span><span class="detail-value">{{ drawerApp.activeSecrets || 0 }}</span></div>
            <div class="detail-row"><span class="detail-label">Certificates</span><span class="detail-value">{{ drawerApp.activeCerts || 0 }}</span></div>
          </div>

          <div class="drawer-section" v-if="drawerOwners.length > 0">
            <h3>Owners</h3>
            <div class="member-list">
              <div v-for="owner in drawerOwners" :key="owner.ownerId" class="member-item">
                <span class="member-name">{{ owner.displayName }}</span>
                <span class="member-type-badge">{{ owner.ownerType }}</span>
              </div>
            </div>
          </div>

          <div class="drawer-section" v-if="drawerCredentials.length > 0">
            <h3>Credentials</h3>
            <div class="cred-list">
              <div v-for="cred in drawerCredentials" :key="cred.credentialId" class="cred-item">
                <div class="cred-header">
                  <span class="cred-type-badge" :class="cred.credentialType === 'secret' ? 'badge-secret' : 'badge-cert'">{{ cred.credentialType }}</span>
                  <span class="cred-name">{{ cred.displayName || cred.hint || 'Unnamed' }}</span>
                </div>
                <div class="cred-dates">
                  <span>Start: {{ cred.startDateTime || '--' }}</span>
                  <span>End: {{ cred.endDateTime || '--' }}</span>
                  <span class="cred-status" :class="credStatusClass(cred.daysUntilExpiry)">{{ credStatus(cred.daysUntilExpiry) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import FindingSeverityChip from '@/components/entra/common/FindingSeverityChip.vue';
import { getEntraApplications, getEntraApplicationCredentials, getEntraApplicationsSummary, getEntraApplicationDetails } from '@/services/entra-api.js';
import { formatCount } from '@/services/entra-utils.js';

export default {
  name: 'EntraApplications',
  components: { EntraPageHeader, MetricCard, FindingSeverityChip },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      summary: { appRegistrations: 0, enterpriseApps: 0, managedIdentities: 0, multiTenantApps: 0, noOwner: 0, secretsExpiring30: 0, certsExpiring30: 0, expiredCredentials: 0, disabledServicePrincipals: 0, highPrivilegePermissions: 0, appsWithFindings: 0 },
      summaryState: 'loading',
      apps: [], tableState: 'loading',
      credentials: [],
      error: '',
      showDrawer: false,
      drawerState: 'loading',
      drawerError: '',
      drawerApp: {},
      drawerOwners: [],
      drawerCredentials: []
    };
  },
  methods: {
    formatCount: formatCount,
    credStatusClass: function(days) {
      if (days === null || days === undefined) return '';
      if (days <= 0) return 'cred-expired';
      if (days <= 30) return 'cred-expiring-soon';
      return 'cred-valid';
    },
    credStatus: function(days) {
      if (days === null || days === undefined) return 'Unknown';
      if (days <= 0) return 'Expired';
      if (days <= 7) return 'Expiring (7d)';
      if (days <= 30) return 'Expiring (30d)';
      if (days <= 90) return 'Expiring (90d)';
      return 'Valid';
    },
    handleExport: function() {},
    openApp: function(appId) {
      var self = this;
      this.showDrawer = true;
      this.drawerState = 'loading';
      this.drawerError = '';
      this.drawerApp = {};
      this.drawerOwners = [];
      this.drawerCredentials = [];

      getEntraApplicationDetails(this.tenantId, appId).then(function(data) {
        if (data.error) {
          self.drawerError = data.error.message || 'Application not found.';
          self.drawerState = 'error';
          return;
        }
        self.drawerApp = data;
        self.drawerOwners = data.owners || [];
        self.drawerCredentials = data.credentials || [];
        self.drawerState = 'loaded';
      }).catch(function(err) {
        self.drawerError = err.message || 'Failed to load application details.';
        self.drawerState = 'error';
      });
    },
    closeDrawer: function() {
      this.showDrawer = false;
    },
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.summaryState = 'empty';
        this.tableState = 'empty';
        return;
      }

      getEntraApplications(this.tenantId).then(function(data) {
        self.apps = data.items || [];
        self.tableState = self.apps.length === 0 ? 'empty' : 'loaded';
      }).catch(function(err) { self.error = err.message; self.tableState = 'error'; });

      getEntraApplicationCredentials(this.tenantId, { sort: 'endDateTime' }).then(function(data) {
        self.credentials = (data.items || []).slice(0, 50);
      }).catch(function() {});

      getEntraApplicationsSummary(this.tenantId).then(function(data) {
        self.summary = data;
        self.summaryState = 'loaded';
      }).catch(function() { self.summaryState = 'error'; });
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; }
.page-body { padding: 24px; }
.kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 12px; margin-bottom: 24px; }
.section-title { font-size: 1rem; font-weight: 600; color: #1e293b; margin: 24px 0 12px 0; }
.error-message { padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #991b1b; font-size: 0.875rem; }
.loading-state { padding: 48px; text-align: center; color: #94a3b8; }
.table-wrapper { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; text-align: left; padding: 10px 12px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.app-name { font-weight: 600; color: #1e293b; }
.app-id { font-size: 0.6875rem; color: #94a3b8; font-family: monospace; }
.expiring-cell { color: #dc2626; font-weight: 600; }
.expiry-soon { color: #dc2626; font-weight: 600; }
.expired { color: #991b1b; font-weight: 700; }
.cred-status { font-size: 0.6875rem; font-weight: 600; padding: 2px 6px; border-radius: 4px; }
.cred-expired { background: #fee2e2; color: #991b1b; }
.cred-expiring-soon { background: #fef3c7; color: #92400e; }
.cred-valid { background: #dcfce7; color: #166534; }
.detail-btn { background: none; border: 1px solid #cbd5e1; padding: 4px 10px; border-radius: 4px; font-size: 0.75rem; color: #475569; cursor: pointer; font-family: 'Inter', sans-serif; }

.drawer-overlay { position: fixed; top: 0; right: 0; bottom: 0; left: 0; background: rgba(0,0,0,0.3); z-index: 100; display: flex; justify-content: flex-end; }
.drawer { width: 520px; max-width: 90vw; height: 100%; background: #ffffff; overflow-y: auto; box-shadow: -4px 0 24px rgba(0,0,0,0.12); animation: drawer-slide 0.2s ease-out; }
@keyframes drawer-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
.drawer-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; background: #ffffff; z-index: 1; }
.drawer-header h2 { margin: 0; font-size: 1.125rem; font-weight: 600; color: #1e293b; }
.drawer-close { background: none; border: none; font-size: 1.5rem; color: #94a3b8; cursor: pointer; padding: 0 4px; line-height: 1; }
.drawer-close:hover { color: #475569; }
.drawer-loading { padding: 24px; text-align: center; color: #94a3b8; }
.drawer-error { padding: 24px; color: #991b1b; }
.drawer-body { padding: 24px; }
.drawer-details { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
.detail-row { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 8px; border-bottom: 1px solid #f1f5f9; }
.detail-label { font-size: 0.75rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; }
.detail-value { font-size: 0.8125rem; color: #334155; text-align: right; max-width: 60%; word-break: break-all; }
.detail-value.mono { font-family: monospace; font-size: 0.6875rem; }
.drawer-section { margin-top: 20px; }
.drawer-section h3 { font-size: 0.875rem; font-weight: 600; color: #475569; margin: 0 0 10px 0; }
.member-list { display: flex; flex-direction: column; gap: 6px; }
.member-item { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px solid #f8fafc; }
.member-name { font-size: 0.8125rem; color: #334155; font-weight: 500; }
.member-type-badge { margin-left: auto; font-size: 0.625rem; padding: 1px 6px; border-radius: 3px; background: #f1f5f9; color: #64748b; text-transform: uppercase; font-weight: 600; }
.cred-list { display: flex; flex-direction: column; gap: 10px; }
.cred-item { padding: 10px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0; }
.cred-header { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.cred-type-badge { font-size: 0.625rem; padding: 1px 6px; border-radius: 3px; font-weight: 600; text-transform: uppercase; }
.badge-secret { background: #fef3c7; color: #92400e; }
.badge-cert { background: #dbeafe; color: #1e40af; }
.cred-name { font-size: 0.8125rem; color: #334155; font-weight: 500; }
.cred-dates { display: flex; gap: 16px; font-size: 0.6875rem; color: #94a3b8; flex-wrap: wrap; }
.cred-dates .cred-status { font-weight: 600; font-size: 0.6875rem; padding: 1px 6px; border-radius: 3px; }
.cred-expired { color: #991b1b; }
.cred-expiring-soon { color: #92400e; }
.cred-valid { color: #166534; }
</style>
